// src/components/SkillSelector.js
// Compact dropdown for selecting a skill in the chat input area.
// Sits next to the textarea — shows the active skill name and allows switching.

import React, { useState, useRef, useEffect } from 'react';
import '../styles/SkillSelector.css';

const SkillSelector = ({
  skills = [],
  selectedSkillId,
  onSelectSkill,
  onOpenSkillsManager,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  // Determine what's active
  const defaultSkill = skills.find(s => s.is_default);
  const activeSkill = selectedSkillId
    ? skills.find(s => s.skill_id === selectedSkillId)
    : defaultSkill;

  const activeLabel = activeSkill ? activeSkill.name : 'No Skill';

  const handleSelect = (skillId) => {
    onSelectSkill(skillId);
    setOpen(false);
  };

  const handleClear = () => {
    onSelectSkill(null);
    setOpen(false);
  };

  if (skills.length === 0) {
    // Show a minimal trigger that links to Skills Manager
    return (
      <div className="skill-selector" ref={dropdownRef}>
        <button
          className="skill-selector__trigger"
          onClick={() => { if (onOpenSkillsManager) onOpenSkillsManager(); }}
          disabled={disabled}
          title="Create your first skill"
          type="button"
        >
          <span className="skill-selector__icon">⚡</span>
          <span className="skill-selector__label">Add Skill</span>
        </button>
      </div>
    );
  }

  return (
    <div className="skill-selector" ref={dropdownRef}>
      <button
        className={`skill-selector__trigger ${activeSkill ? 'skill-selector__trigger--active' : ''}`}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        title={activeSkill ? `Active skill: ${activeSkill.name}` : 'Select a skill'}
        type="button"
      >
        <span className="skill-selector__icon">⚡</span>
        <span className="skill-selector__label">{activeLabel}</span>
        <svg className="skill-selector__caret" viewBox="0 0 24 24" width="14" height="14">
          <path fill="currentColor" d="M7 10l5 5 5-5z" />
        </svg>
      </button>

      {open && (
        <div className="skill-selector__dropdown">
          <div className="skill-selector__header">
            <span className="skill-selector__header-text">Select Skill</span>
          </div>

          {/* No skill option */}
          <button
            className={`skill-selector__option ${!activeSkill ? 'skill-selector__option--active' : ''}`}
            onClick={handleClear}
          >
            <span className="skill-selector__option-icon">○</span>
            <span className="skill-selector__option-name">No Skill</span>
            {!activeSkill && (
              <svg className="skill-selector__check" viewBox="0 0 24 24" width="14" height="14">
                <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            )}
          </button>

          {/* Divider */}
          <div className="skill-selector__divider" />

          {/* Skill options */}
          {skills.map(skill => (
            <button
              key={skill.skill_id}
              className={`skill-selector__option ${
                (selectedSkillId === skill.skill_id || (!selectedSkillId && skill.is_default))
                  ? 'skill-selector__option--active'
                  : ''
              }`}
              onClick={() => handleSelect(skill.skill_id)}
            >
              <span className="skill-selector__option-icon">
                {skill.is_default ? '★' : '☆'}
              </span>
              <div className="skill-selector__option-details">
                <span className="skill-selector__option-name">{skill.name}</span>
                {skill.is_default && (
                  <span className="skill-selector__default-badge">default</span>
                )}
              </div>
              {(selectedSkillId === skill.skill_id || (!selectedSkillId && skill.is_default)) && (
                <svg className="skill-selector__check" viewBox="0 0 24 24" width="14" height="14">
                  <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              )}
            </button>
          ))}

          {/* Manage skills link */}
          <div className="skill-selector__divider" />
          <button
            className="skill-selector__manage"
            onClick={() => {
              setOpen(false);
              if (onOpenSkillsManager) onOpenSkillsManager();
            }}
          >
            <span className="skill-selector__manage-icon">⚙️</span>
            <span>Manage Skills</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default SkillSelector;
